import React from 'react';
import ReactDOM from 'react-dom';
import { nanoid } from 'nanoid';
import { NodeSelection, Plugin, PluginKey } from 'prosemirror-state';

import { BaseExtension, EditorOptions, extensionType } from '@lyearn/lygo-core';

import { getElementPos, removeMenuContainer, setPluginMeta } from '../../utils';
import { displayReplaceMenuItem } from '../BlockMenu/utils/actions';
import { BLOCK_MENU_BUTTON_POPPER_ID, BlockMenuButtonComponent } from './BlockMenuButton';
import {
  getBlockElmentOnHover,
  getBlockMenuButtonDefaultProps,
  isBlockmenuOpen,
  showBlockMenuButton,
} from './utils';

export const blockMenuButtonkey = new PluginKey('block-menu-button-plugin');

const BLOCK_MENU_BUTTON_CONTAINER_ID = 'block-menu-button-container';

export default class BlockMenuButton extends BaseExtension {
  get type(): string {
    return extensionType.toolbar;
  }

  get name(): string {
    return 'block_menu_button';
  }

  plugins({ portalApi, isLocalizedContent, isMicroLearning }: EditorOptions) {
    return [
      new Plugin({
        key: blockMenuButtonkey,
        state: {
          init() {
            return getBlockMenuButtonDefaultProps(isLocalizedContent);
          },
          apply(tr, old) {
            const props = tr.getMeta(blockMenuButtonkey);
            return props || old;
          },
        },
        view: (editorView) => {
          // This will be called only once. It will add a container div.
          // Block Menu Button will be rendered in this container div.
          let blockMenuButtonContainer = document.createElement('div');
          blockMenuButtonContainer.id = BLOCK_MENU_BUTTON_CONTAINER_ID;
          editorView?.dom?.parentNode?.appendChild(blockMenuButtonContainer);
          const editorContainer = document.querySelector('.editor-container');

          const hideBlockMenuButtonEventhandle = (event: Event) => {
            if (isBlockmenuOpen(editorView)) {
              return;
            }
            if (
              (event as MouseEvent).relatedTarget &&
              !((event as MouseEvent).relatedTarget as Element).closest(
                `#${BLOCK_MENU_BUTTON_POPPER_ID}`,
              )
            ) {
              if (blockMenuButtonkey.getState(editorView.state)?.open) {
                setPluginMeta(
                  editorView,
                  blockMenuButtonkey,
                  getBlockMenuButtonDefaultProps(isLocalizedContent),
                );
              }
            }
          };

          editorContainer?.addEventListener('mouseleave', hideBlockMenuButtonEventhandle);

          const portalKey = nanoid();
          // Need to return the following functions in the view function
          return {
            update: (view) => {
              const state = view.state;
              let props = blockMenuButtonkey.getState(state);
              props.view = view;
              const dom = editorView?.dom?.parentNode?.querySelector(
                `#${BLOCK_MENU_BUTTON_CONTAINER_ID}`,
              );
              const showMenueButton = showBlockMenuButton(props);
              if (dom && showMenueButton && !isMicroLearning) {
                portalApi.createPortal(
                  ReactDOM.createPortal(<BlockMenuButtonComponent {...props} />, dom, portalKey),
                );
              }
            },
            destroy: () => {
              editorContainer?.removeEventListener('mouseleave', hideBlockMenuButtonEventhandle);
              portalApi.destroyPortal(portalKey);
              removeMenuContainer(BLOCK_MENU_BUTTON_CONTAINER_ID, editorView);
            },
          };
        },

        props: {
          handleDOMEvents: {
            mouseover: (editorView, event) => {
              if (isBlockmenuOpen(editorView)) {
                return false;
              }
              const ele = getBlockElmentOnHover(event.target);
              let props = {
                open: false,
                anchorEl: null,
                isLocalizedContent,
              };
              if (ele) {
                props.open = true;
                props.anchorEl = ele;
              }
              setPluginMeta(editorView, blockMenuButtonkey, props);
              return false;
            },
          },
        },
      }),
    ];
  }
}
